import { useAuth } from "@/components/AuthContext";
import { createLogger } from "@/lib/logger";
import { supabase } from "@/lib/supabaseClient";
import { useCallback, useEffect, useState } from "react";

const logger = createLogger("useClassEnrollment");
const getSelectedClassStorageKey = (userId: string) => `chickytutor:selected-class-id:${userId}`;

interface ClassPreferences {
  id: string;
  class_name: string;
  language_target: string;
  cefr_level: string;
  grammar_focus?: string;
  topic_scenario?: string;
  vocabulary_focus?: string;
  student_group_type?: string;
  industry_context?: string;
}

interface EnrolledClass {
  class_id: string;
  class_name: string;
  language_target: string;
  enrolled_at: string;
}

export function useClassEnrollment() {
  const { user } = useAuth();
  const [enrolledClasses, setEnrolledClasses] = useState<EnrolledClass[]>([]);
  const [selectedClassId, setSelectedClassId] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectClass = useCallback(
    (classId: string | null) => {
      setSelectedClassId(classId);
      if (!user || typeof window === "undefined") return;

      const storageKey = getSelectedClassStorageKey(user.id);

      if (classId) {
        window.localStorage.setItem(storageKey, classId);
      } else {
        window.localStorage.removeItem(storageKey);
      }
    },
    [user]
  );

  useEffect(() => {
    if (!user || typeof window === "undefined") {
      setSelectedClassId(null);
      return;
    }

    const storedClassId = window.localStorage.getItem(getSelectedClassStorageKey(user.id));
    if (storedClassId) setSelectedClassId(storedClassId);
  }, [user]);

  // Fetch enrolled classes for authenticated user
  const fetchEnrolledClasses = useCallback(async () => {
    if (!user) {
      setEnrolledClasses([]);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);

      const { data: enrollments, error: fetchError } = await supabase
        .from("class_enrollments")
        .select(
          `
          class_id,
          enrolled_at,
          teacher_classes:class_id (
            id,
            class_name,
            language_target
          )
        `
        )
        .eq("student_id", user.id)
        .order("enrolled_at", { ascending: false });

      if (fetchError) {
        logger.error("Failed to fetch enrolled classes", { error: fetchError });
        setError("Failed to load your classes");
        return;
      }

      const classes = (enrollments || []).map((e: any) => ({
        class_id: e.class_id,
        class_name: e.teacher_classes?.class_name || "Unknown Class",
        language_target: e.teacher_classes?.language_target || "",
        enrolled_at: e.enrolled_at,
      }));

      setEnrolledClasses(classes);

      if (typeof window !== "undefined") {
        const storageKey = getSelectedClassStorageKey(user.id);
        const storedClassId = window.localStorage.getItem(storageKey);
        if (storedClassId && !classes.some((classroom) => classroom.class_id === storedClassId)) {
          window.localStorage.removeItem(storageKey);
          setSelectedClassId(null);
        }
      }

      logger.info("Enrolled classes loaded", { count: classes.length });
    } catch (err) {
      logger.error("Error fetching enrolled classes", { error: err });
      setError("An error occurred while loading classes");
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Join a class using class code
  const joinClass = useCallback(
    async (classCode: string) => {
      if (!user) {
        setError("You must be signed in to join a class");
        return { success: false };
      }

      try {
        setIsLoading(true);
        setError(null);

        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.access_token) {
          setError("Authentication required");
          return { success: false };
        }

        const response = await fetch("/api/classes/join", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ classCode }),
        });

        const data = (await response.json()) as any;

        if (!response.ok) {
          setError("We couldn't join this class. Check the class code and try again.");
          return { success: false };
        }

        logger.info("Successfully joined class", { classCode });

        // Refresh enrolled classes
        await fetchEnrolledClasses();

        return {
          success: true,
          class: data.class,
          alreadyEnrolled: data.alreadyEnrolled,
        };
      } catch (err) {
        logger.error("Error joining class", { error: err });
        setError("An error occurred while joining the class");
        return { success: false };
      } finally {
        setIsLoading(false);
      }
    },
    [user, fetchEnrolledClasses]
  );

  // Get class preferences (for session initialization)
  const getClassPreferences = useCallback(
    async (classId: string): Promise<ClassPreferences | null> => {
      if (!user) {
        return null;
      }

      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.access_token) {
          return null;
        }

        const response = await fetch(`/api/classes/${classId}/preferences`, {
          headers: {
            Authorization: `Bearer ${session.access_token}`,
          },
        });

        if (!response.ok) {
          logger.error("Failed to fetch class preferences", { classId });
          return null;
        }

        const data = (await response.json()) as any;
        return data.preferences;
      } catch (err) {
        logger.error("Error fetching class preferences", { error: err, classId });
        return null;
      }
    },
    [user]
  );

  // Load enrolled classes on mount
  useEffect(() => {
    fetchEnrolledClasses();
  }, [fetchEnrolledClasses]);

  return {
    enrolledClasses,
    selectedClassId,
    setSelectedClassId: selectClass,
    isLoading,
    error,
    joinClass,
    getClassPreferences,
    refreshClasses: fetchEnrolledClasses,
  };
}
